import { ActivityStatus } from "@cca/components";
import { ActivityProgressDto } from "@core/typesV2";
import { indexBy } from "remeda";

import { useActivityContentQuery } from "./useActivityContentQuery";
import { useAssetProgressQuery } from "./useAssetProgressQuery";

export type UseActivityPageLimitParams = {
  courseId: string;
  activityId: string;
  activityProgress: ActivityProgressDto | null | undefined;
  userIsLearner: boolean;
};

export function useActivityLastVisitedPage({
  courseId,
  activityId,
  userIsLearner,
  activityProgress,
}: UseActivityPageLimitParams) {
  const activityIsInProgress =
    activityProgress?.status === ActivityStatus.InProgress;

  const { data: activityContent } = useActivityContentQuery({
    courseId,
    activityId,
    attempt: activityProgress?.attempt,
    enabled: userIsLearner && activityIsInProgress,
  });
  const { data: assetProgress } = useAssetProgressQuery(
    { courseId, activityId },
    { enabled: userIsLearner && activityIsInProgress },
  );

  if (
    activityProgress &&
    activityProgress.status !== ActivityStatus.InProgress
  ) {
    return 1;
  }

  if (!userIsLearner || !activityContent || !assetProgress) return undefined;

  const assetIdsByPage = (activityContent.activity.pages ?? []).map(
    (p) => p.simpleLayout?.assets ?? [],
  );

  const assetProgressById = indexBy(assetProgress, (p) => p.assetId);

  const lastVisitedPageIndex = assetIdsByPage.reduce(
    (lastIdx, pageAssetIds, idx) => {
      const pageHasVisited = pageAssetIds.some(
        (assetId) => !!assetProgressById[assetId]?.visitedOn,
      );

      return pageHasVisited ? idx : lastIdx;
    },
    -1,
  );

  return lastVisitedPageIndex === -1 ? 1 : lastVisitedPageIndex + 1;
}
